import {
  AuthDesktopCard,
  AuthDesktopHeader,
  AuthDesktopLayout,
  AuthScreenContent,
  AuthScreenFooter,
  AuthScreenHeader,
  AuthScreenLayout,
  AuthScreenSpacer,
  authScreenStyles,
} from "@/components/auth/auth-screen-layout";
import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OAuth } from "@/components/ui/oauth";
import { BrandColors } from "@/constants/theme";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { LoginFormData, loginSchema } from "@/lib/validation/auth";
import Ionicons from "@react-native-vector-icons/ionicons";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, router } from "expo-router";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { HelperText, TextInput as PaperTextInput } from "react-native-paper";
import { useAuth } from "../../contexts/auth-context";
import { showToast } from "../../lib/toast";

export default function LoginScreen() {
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const desktop = useDesktopLayout();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      let user;
      user = await signIn(data.email, data.password);
      if (user.role === "driver" && user.email_verified_at) {
        const needsOnboarding =
          !user.stripe_account?.details_submitted ||
          !user.driver?.is_verified ||(user.vehicles_count || 0) ===
            0;
        if (needsOnboarding) {
          router.replace("/complete-registration");
          return;
        }
      }
    } catch (error: any) {
      showToast.error(error.response?.data?.message || "Invalid credentials");
    }
  };

  const handleBack = () => {};

  const inputStyle = [authScreenStyles.input, desktop && authScreenStyles.desktopInput];
  const inputOutlineStyle = desktop
    ? authScreenStyles.desktopInputOutline
    : authScreenStyles.inputOutline;

  const formFields = (
    <View style={desktop ? authScreenStyles.desktopForm : authScreenStyles.form}>
      <View style={authScreenStyles.field}>
        <Label label={desktop ? "Email address" : "E-mail"} />
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              placeholder={desktop ? "you@example.com" : "Enter your email"}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              error={!!errors.email}
              style={inputStyle}
              outlineStyle={inputOutlineStyle}
              testID="email"
            />
          )}
        />
        {errors.email && (
          <HelperText type="error" visible={!!errors.email}>
            {errors.email?.message}
          </HelperText>
        )}
      </View>

      <View style={authScreenStyles.field}>
        <Label label="Password" />
        <Controller
          control={control}
          name="password"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              placeholder={desktop ? "••••••••" : "Enter your password"}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              secureTextEntry={!showPassword}
              autoComplete="password"
              error={!!errors.password}
              style={inputStyle}
              outlineStyle={inputOutlineStyle}
              right={
                <PaperTextInput.Icon
                  icon={showPassword ? "eye-off" : "eye"}
                  size={desktop ? 18 : undefined}
                  color={desktop ? BrandColors.mediumGray : undefined}
                  onPress={() => setShowPassword(!showPassword)}
                />
              }
              testID="password"
            />
          )}
        />
        {errors.password && (
          <HelperText type="error" visible={!!errors.password}>
            {errors.password?.message}
          </HelperText>
        )}
      </View>
      <Link href="/(auth)/forgot-password" asChild>
        <TouchableOpacity>
          <ThemedText style={styles.forgotPassword} type="smallSemiBold">
            Forgot Password?
          </ThemedText>
        </TouchableOpacity>
      </Link>
    </View>
  );

  const continueButton = (
    <BrandButton
      variant="primary"
      loading={isSubmitting}
      onPress={handleSubmit(onSubmit)}
      style={desktop ? authScreenStyles.desktopContinueButton : authScreenStyles.continueButton}
      labelStyle={
        desktop ? authScreenStyles.desktopContinueButtonLabel : authScreenStyles.continueButtonLabel
      }
      contentStyle={authScreenStyles.continueButtonContent}
      icon={() => (
        <Ionicons
          name="chevron-forward"
          size={desktop ? 14 : 18}
          color={BrandColors.white}
        />
      )}
    >
      {isSubmitting ? "Signing in..." : "Continue"}
    </BrandButton>
  );

  const signupFooter = (
    <AuthScreenFooter>
      <ThemedText type="small">Don&apos;t have an account? </ThemedText>
      <Link href="/(auth)/register" asChild>
        <TouchableOpacity accessibilityLabel="goto-register">
          <ThemedText style={authScreenStyles.link} type="smallSemiBold">
            Signup
          </ThemedText>
        </TouchableOpacity>
      </Link>
    </AuthScreenFooter>
  );

  if (desktop) {
    return (
      <AuthDesktopLayout
        onBack={undefined}
      >
        <AuthDesktopHeader title="Login" subtitle="Login to get started" />
        <AuthDesktopCard>
          {formFields}
          <OAuth variant="labeled" />
          {continueButton}
          {signupFooter}
        </AuthDesktopCard>
      </AuthDesktopLayout>
    );
  }

  return (
    <AuthScreenLayout onBack={handleBack} showBackButton={false} keyboardInset>
      <AuthScreenContent>
        <AuthScreenHeader title="Login" subtitle="Login to get started" />

        <OAuth />

        {formFields}
      </AuthScreenContent>

      <AuthScreenSpacer />

      <AuthScreenContent>{continueButton}</AuthScreenContent>

      {signupFooter}
    </AuthScreenLayout>
  );
}

const styles = StyleSheet.create({
  forgotPassword: {
    fontSize: 14,
    color: BrandColors.primary,
    fontWeight: "600",
    textAlign: "right",
  },
});
